import {logger} from './logger';

/**
 * Media session actions, including the conferencing specific ones which are
 * not part of the `MediaSessionAction` type shipped with TypeScript's DOM
 * library.
 *
 * `hangup`, `togglecamera`, `togglemicrophone` and `enterpictureinpicture` are
 * implemented by Chromium based browsers. `togglescreenshare` and
 * `voiceactivity` are only specified so far, and are registered as a no-op by
 * every current browser, which {@link createMediaSession} handles gracefully.
 *
 * Note that `voiceactivity` runs the other way around to the rest: the user
 * agent invokes the handler when it detects voice on a live microphone track,
 * e.g. to let a page nudge a muted user who is talking.
 *
 * @see {@link https://w3c.github.io/mediasession/#enumdef-mediasessionaction}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaSession/setActionHandler}
 */
export type ExtendedMediaSessionAction =
    | MediaSessionAction
    | 'enterpictureinpicture'
    | 'hangup'
    | 'togglecamera'
    | 'togglemicrophone'
    | 'togglescreenshare'
    | 'voiceactivity';

/**
 * Why the user agent asked the page to enter picture in picture.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaSession/setActionHandler}
 */
export type MediaSessionEnterPictureInPictureReason =
    'other' | 'useraction' | 'contentoccluded';

/**
 * Details passed to the `enterpictureinpicture` action handler, which are not
 * part of the `MediaSessionActionDetails` type shipped with TypeScript's DOM
 * library.
 */
export interface MediaSessionEnterPictureInPictureActionDetails extends MediaSessionActionDetails {
    enterPictureInPictureReason?: MediaSessionEnterPictureInPictureReason;
}

/**
 * The subset of the `navigator.mediaSession` API used by
 * {@link createMediaSession}, so that an equivalent implementation can be
 * injected, e.g. in tests.
 */
export interface MediaSessionLike {
    /**
     * Descriptive information about the currently played media.
     */
    metadata: MediaMetadata | null;
    /**
     * Whether the media session is currently playing or paused.
     */
    playbackState: MediaSessionPlaybackState;
    /**
     * Register, or unregister when the handler is `null`, a handler for the
     * given media session action.
     */
    setActionHandler(
        action: ExtendedMediaSessionAction,
        handler: MediaSessionActionHandler | null,
    ): void;
    /**
     * Tell the user agent whether the camera is considered to be active.
     *
     * P.S. Safari's impl. is an imperative action to change the track's
     * muted stated whereas Chromium-based browsers indicate to the user
     * agent the microphone capture state desired by the page
     */
    setCameraActive?(active: boolean): Promise<void>;
    /**
     * Tell the user agent whether the microphone is considered to be active.
     *
     * P.S. Safari's impl. is an imperative action to change the track's
     * muted stated whereas Chromium-based browsers indicate to the user
     * agent the microphone capture state desired by the page
     */
    setMicrophoneActive?(active: boolean): Promise<void>;
    /**
     * Tell the user agent whether the screen is considered to be shared. Only
     * specified so far, no browser implements it yet.
     */
    setScreenshareActive?(active: boolean): Promise<void>;
    /**
     * Present the playback position, or reset it when called without a state.
     * Throws a `TypeError` for a state the user agent considers invalid.
     */
    setPositionState?(state?: MediaPositionState): void;
}

/**
 * Descriptive information presented by the platform's media controls, e.g. the
 * lock screen or the notification shade.
 */
export interface MediaSessionMetadataInit {
    /**
     * Main label, e.g. the name of the meeting.
     */
    title: string;
    /**
     * Secondary label, e.g. the name of the brand.
     */
    artist?: string;
    /**
     * Tertiary label.
     */
    album?: string;
    /**
     * Images associated with the session, e.g. the brand logo.
     */
    artwork?: MediaImage[];
}

/**
 * Options to create a media session.
 */
export interface MediaSessionInit {
    /**
     * Handlers to register, keyed by the action they handle. Actions without a
     * handler are left untouched, allowing the platform to fall back to its
     * default behavior.
     */
    handlers?: Partial<
        Record<ExtendedMediaSessionAction, MediaSessionActionHandler>
    >;
    /**
     * Initial metadata to present.
     */
    metadata?: MediaSessionMetadataInit;
    /**
     * Initial playback state, defaults to `playing`.
     */
    playbackState?: MediaSessionPlaybackState;
    /**
     * Initial playback position to present.
     */
    positionState?: MediaPositionState;
    /**
     * Apply the capture state even when the platform implements it as a
     * command that mutes the real tracks, defaults to `false`.
     *
     * Only enable this when every call is made from a user gesture handler,
     * otherwise unmuting is rejected and the capture stays muted.
     */
    applyImperativeCaptureState?: boolean;
    /**
     * The media session implementation to use, defaults to
     * `navigator.mediaSession` when available.
     */
    mediaSession?: MediaSessionLike;
    /**
     * Factory building the metadata object, defaults to the `MediaMetadata`
     * constructor when available.
     */
    createMetadata?: (init: MediaSessionMetadataInit) => MediaMetadata;
}

/**
 * Controls an active media session.
 */
export interface MediaSessionController {
    /**
     * Whether the media session API is available in the current environment.
     * Every other method is a no-op when it is `false`.
     */
    readonly isSupported: boolean;
    /**
     * Update, or clear when omitted, the presented metadata.
     */
    setMetadata: (metadata?: MediaSessionMetadataInit) => void;
    /**
     * Update the presented playback state.
     */
    setPlaybackState: (playbackState: MediaSessionPlaybackState) => void;
    /**
     * Update whether the platform should present the microphone as active.
     *
     * A no-op on platforms that implement this as a command mutating the real
     * tracks, unless `applyImperativeCaptureState` was set.
     */
    setMicrophoneActive: (active: boolean) => Promise<void>;
    /**
     * Update whether the platform should present the camera as active, with
     * the same caveat as {@link MediaSessionController.setMicrophoneActive}.
     */
    setCameraActive: (active: boolean) => Promise<void>;
    /**
     * Update whether the platform should present the screen as shared, with
     * the same caveat as {@link MediaSessionController.setMicrophoneActive}.
     *
     * Only implemented by WebKit so far.
     */
    setScreenshareActive: (active: boolean) => Promise<void>;
    /**
     * Update, or reset when omitted, the presented playback position.
     *
     * Pass `duration: Infinity` for a live stream such as a call, which makes
     * the platform present an elapsed clock rather than a seekable scrub bar.
     * States the user agent rejects are logged and otherwise ignored.
     */
    setPositionState: (state?: MediaPositionState) => void;
    registerActions(
        handlers: Partial<
            Record<ExtendedMediaSessionAction, MediaSessionActionHandler>
        >,
    ): void;
    deregisterActions(actions: ExtendedMediaSessionAction[]): void;
    /**
     * Unregister every handler registered by this session and reset the
     * metadata, the playback state and the position state.
     *
     * Terminal and idempotent: every other method becomes a no-op afterwards,
     * so a slower owner cannot resurrect the platform's media controls.
     */
    destroy: () => void;
}

const getDefaultMediaSession = (): MediaSessionLike | undefined =>
    typeof navigator !== 'undefined' && 'mediaSession' in navigator
        ? navigator.mediaSession
        : undefined;

const getDefaultCreateMetadata = ():
    ((init: MediaSessionMetadataInit) => MediaMetadata) | undefined =>
    typeof MediaMetadata === 'function'
        ? init => new MediaMetadata(init)
        : undefined;

/**
 * Whether the platform treats the capture state setters as a command rather
 * than as a report.
 *
 * Chromium's `setMicrophoneActive`/`setCameraActive` are purely declarative:
 * they only choose which glyph the media control draws. WebKit's are
 * imperative, and route through `UserMediaController::updateCaptureState` to
 * the page level capture muted state, which mutes the real tracks. Activating
 * capture there additionally requires a visible document and a user gesture,
 * and can raise a permission prompt.
 *
 * That asymmetry is a trap for a state driven caller: muting always succeeds,
 * while the matching unmute is rejected outside a gesture, leaving the
 * microphone muted at the page level with no way back from a signal handler.
 *
 * `setScreenshareActive` distinguishes the two, as only WebKit implements it.
 * WebKit exposes all three under a single `MediaSessionCaptureToggleAPIEnabled`
 * setting, so they appear and disappear together, which makes the probe
 * reliable: when the setting is off none of them exist and there is nothing to
 * call, and Chromium only ever exposes the microphone and the camera ones.
 */
const hasImperativeCaptureState = (mediaSession: MediaSessionLike) =>
    typeof mediaSession.setScreenshareActive === 'function';

const createUnsupportedMediaSession = (): MediaSessionController => ({
    isSupported: false,
    setMetadata: () => undefined,
    setPlaybackState: () => undefined,
    setMicrophoneActive: () => Promise.resolve(),
    setCameraActive: () => Promise.resolve(),
    setScreenshareActive: () => Promise.resolve(),
    setPositionState: () => undefined,
    registerActions: () => undefined,
    deregisterActions: () => undefined,
    destroy: () => undefined,
});

/**
 * Create a media session, presenting the ongoing call to the platform's media
 * controls, e.g. the lock screen, the notification shade or headset buttons.
 *
 * Declaring a media session also hints the browser that the page is playing
 * media, which mobile browsers use to relax the throttling applied to
 * backgrounded pages and to keep the audio capture alive.
 *
 * Every method is a no-op when the Media Session API is unavailable, so the
 * caller does not need to feature detect it.
 *
 * @example
 * ```ts
 * const session = createMediaSession({
 *     metadata: {title: 'Weekly sync', artist: 'Pexip'},
 *     handlers: {
 *         hangup: () => leaveMeeting(),
 *         togglemicrophone: () => toggleAudioMuted(),
 *     },
 * });
 * await session.setMicrophoneActive(true);
 * session.destroy();
 * ```
 */
export const createMediaSession = ({
    handlers = {},
    metadata,
    playbackState = 'playing',
    positionState,
    applyImperativeCaptureState = false,
    mediaSession = getDefaultMediaSession(),
    createMetadata = getDefaultCreateMetadata(),
}: MediaSessionInit = {}): MediaSessionController => {
    if (!mediaSession) {
        logger.debug(
            {context: 'MediaSession'},
            'Media Session API is not supported',
        );
        return createUnsupportedMediaSession();
    }

    const registeredActions = new Set<ExtendedMediaSessionAction>();
    let destroyed = false;

    const skipCaptureState =
        !applyImperativeCaptureState && hasImperativeCaptureState(mediaSession);
    if (skipCaptureState) {
        logger.debug(
            {context: 'MediaSession'},
            'Skipping the media session capture state, the platform applies it to the real tracks',
        );
    }

    const setActionHandler = (
        action: ExtendedMediaSessionAction,
        handler: MediaSessionActionHandler | null,
    ) => {
        try {
            mediaSession.setActionHandler(action, handler);
            return true;
        } catch (error: unknown) {
            logger.debug(
                {context: 'MediaSession', action, error},
                'Media session action is not supported',
            );
            return false;
        }
    };

    const setMetadata = (updatedMetadata?: MediaSessionMetadataInit) => {
        if (destroyed) {
            return;
        }
        if (!updatedMetadata || !createMetadata) {
            mediaSession.metadata = null;
            return;
        }
        mediaSession.metadata = createMetadata(updatedMetadata);
    };

    const setPlaybackState = (
        updatedPlaybackState: MediaSessionPlaybackState,
    ) => {
        if (destroyed) {
            return;
        }
        mediaSession.playbackState = updatedPlaybackState;
    };

    const setMicrophoneActive = async (active: boolean) => {
        if (
            destroyed ||
            skipCaptureState ||
            !mediaSession.setMicrophoneActive
        ) {
            return;
        }
        try {
            await mediaSession.setMicrophoneActive(active);
        } catch (error: unknown) {
            logger.debug(
                {context: 'MediaSession', active, error},
                'Failed to update the media session microphone state',
            );
        }
    };

    const setCameraActive = async (active: boolean) => {
        if (destroyed || skipCaptureState || !mediaSession.setCameraActive) {
            return;
        }
        try {
            await mediaSession.setCameraActive(active);
        } catch (error: unknown) {
            logger.debug(
                {context: 'MediaSession', active, error},
                'Failed to update the media session camera state',
            );
        }
    };

    const setScreenshareActive = async (active: boolean) => {
        if (
            destroyed ||
            skipCaptureState ||
            !mediaSession.setScreenshareActive
        ) {
            return;
        }
        try {
            await mediaSession.setScreenshareActive(active);
        } catch (error: unknown) {
            logger.debug(
                {context: 'MediaSession', active, error},
                'Failed to update the media session screenshare state',
            );
        }
    };

    const setPositionState = (state?: MediaPositionState) => {
        if (destroyed || !mediaSession.setPositionState) {
            return;
        }
        try {
            // Blink rejects a missing, `NaN` or negative duration, a negative
            // position, a position past the duration and a zero playback rate.
            mediaSession.setPositionState(state);
        } catch (error: unknown) {
            logger.debug(
                {context: 'MediaSession', state, error},
                'Failed to update the media session position state',
            );
        }
    };

    const destroy = () => {
        if (destroyed) {
            return;
        }
        for (const action of registeredActions) {
            setActionHandler(action, null);
        }
        registeredActions.clear();
        setMetadata();
        setPlaybackState('none');
        setPositionState();
        // The session is a view onto the global `navigator.mediaSession`, which
        // outlives it, so every mutator has to stop writing to it once the
        // session is gone. Owners tear down on different clocks, e.g. the view
        // layer only unbinds its media element listeners on the next React
        // passive cleanup, well after the service has ended the call.
        destroyed = true;
    };

    const registerActions: MediaSessionController['registerActions'] =
        handlers => {
            if (destroyed) {
                return;
            }
            for (const [action, handler] of Object.entries(handlers)) {
                if (!handler) {
                    continue;
                }
                if (
                    setActionHandler(
                        action as ExtendedMediaSessionAction,
                        handler,
                    )
                ) {
                    registeredActions.add(action as ExtendedMediaSessionAction);
                }
            }
        };

    const deregisterActions: MediaSessionController['deregisterActions'] =
        actions => {
            if (destroyed) {
                return;
            }
            for (const action of actions) {
                if (registeredActions.delete(action)) {
                    setActionHandler(action, null);
                }
            }
        };

    // Register initial actions
    registerActions(handlers);
    setMetadata(metadata);
    mediaSession.playbackState = playbackState;
    if (positionState) {
        setPositionState(positionState);
    }

    return {
        isSupported: true,
        setMetadata,
        setPlaybackState,
        setMicrophoneActive,
        setCameraActive,
        setScreenshareActive,
        setPositionState,
        registerActions,
        deregisterActions,
        destroy,
    };
};
